<script setup>
  import { defineProps, defineEmits } from "vue";

  const props = defineProps({
    modelValue: String,
    tabs: {
      type: Array,
      required: true,
    },
    variant: {
      type: String,
      default: "default", // 'line' | 'box' | 'tag'
    },
  });

  const emits = defineEmits(["update:modelValue"]);

  function selectTab(value) {
    emits("update:modelValue", value);
  }

  const isActive = (value) => props.modelValue === value;
</script>

<template>
  <div class="tab-container" :class="props.variant" v-bind="$attrs">
    <div class="w-con">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        :class="{ active: isActive(tab.value) }"
        :style="tab.style ?? ''"
        class="tab-btn"
        @click="selectTab(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tab-container {
    .w-con {
      display: flex;
    }
    &.default {
      border-radius: 0.5rem;
      border: 1px solid var(--smoe-border);
      overflow: hidden;
      --tab-active-bg-color: var(--smoe-primary-500);
      --tab-active-text-color: var(--smoe-white);
      --tab-bg-color: var(--smoe-white);
      --tab-border-color: var(--smoe-gray-400);
      --tab-hover-bg-color: var(--smoe-gray-100);
      --tab-font-size: 1.25rem;
      --tab-font-weight: 700;
      --tab-height: 56px;
      --tab-text-color: var(--smoe-gray-700);
      .w-con {
        width: 100%;
        max-width: 100%;
        margin: 0;
      }
      .tab-btn {
        flex: 1;
        border-left: 1px solid var(--smoe-gray-400);
        &:first-of-type {
          border-left: 0;
        }
      }
    }
    &.round-shadow {
      --tab-active-bg-color: var(--smoe-primary-500);
      --tab-active-border-color: var(--smoe-primary-500);
      --tab-active-box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.16);
      --tab-active-text-color: var(--smoe-white);
      --tab-bg-color: var(--smoe-white);
      --tab-border-radius: 60px;
      --tab-box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.08);
      --tab-font-size: 1.0625rem;
      --tab-font-weight: 700;
      --tab-height: auto;
      --tab-hover-box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.08);
      --tab-hover-text-color: var(--smoe-primary-500);
      --tab-padding: 0.75rem 2rem;
      --tab-text-color: var(--smoe-gray-700);
      .w-con {
        width: 100%;
        max-width: 100%;
        gap: 0.75rem;
        margin: 0;
        justify-content: center;
      }
      .tab-btn {
        flex: auto 0 0;
        min-width: 150px;
      }
    }
    /* 메인 톤앤매너 언더라인 탭 (시안 B) — 좌측 정렬, 활성 시 보라 언더라인
       (변형 이름을 underline으로 하면 Tailwind .underline 유틸과 충돌하므로 underbar 사용) */
    &.underbar {
      border-bottom: 2px solid #e8eaf1;
      --tab-height: auto;
      --tab-font-size: 15px;
      --tab-font-weight: 600;
      --tab-text-color: #8a8fa3;
      --tab-hover-text-color: #4f46e5;
      --tab-active-bg-color: transparent;
      --tab-active-text-color: #4f46e5;
      --tab-padding: 8px 2px 9px;
      .w-con {
        width: 100%;
        max-width: 100%;
        margin: 0;
        gap: 34px;
        justify-content: flex-start;
      }
      .tab-btn {
        flex: auto 0 0;
        margin-bottom: -2px;
        border-bottom: 3px solid transparent;
        text-decoration: none;
        &.active {
          font-weight: 800;
          border-bottom-color: #4f46e5;
        }
      }
    }
    &.line {
      --tab-height: 60px;
      --tab-font-size: 1.125rem;
      --tab-font-weight: 600;
      --tab-text-color: var(--smoe-gray-700);
      --tab-hover-text-color: var(--smoe-primary-500);
      --tab-active-text-color: var(--smoe-primary-500);
      --tab-padding: 0 1.5rem;
      // border-bottom: 2px solid var(--smoe-border);
      .w-con {
        width: 100%;
        max-width: 100%;
        gap: 0;
        margin: 0;
        justify-content: center;
      }
      .tab-btn {
        position: relative;
        flex: auto 0 0;
        &:before {
          content: "";
          width: 100%;
          height: 2px;
          display: block;
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          background-color: transparent;
          background-color: var(--smoe-gray-400);
          transition: 0.2s ease-in-out;
        }
        &.active {
          &:before {
            height: 4px;
            background-color: var(--smoe-primary-500);
          }
        }
      }
    }

    .tab-btn {
      height: var(--tab-height);
      color: var(--tab-text-color);
      font-size: var(--tab-font-size, 1rem);
      font-weight: var(--tab-font-weight, 500);
      background-color: var(--tab-bg-color, transparent);
      border-radius: var(--tab-border-radius, 0);
      border-width: var(--tab-border-width, 0);
      border-color: var(--tab-border-color, var(--smoe-border));
      box-shadow: var(--tab-box-shadow, none);
      padding: var(--tab-padding, 0);
      transition: 0.2s ease-in-out;
      &:hover {
        color: var(--tab-hover-text-color, var(--tab-text-color));
        background-color: var(--tab-hover-bg-color, var(--tab-bg-color));
        border-color: var(--tab-hover-border-color, var(--tab-border-color));
        box-shadow: var(--tab-hover-box-shadow, none);
      }
      &.active {
        color: var(--tab-active-text-color);
        background-color: var(--tab-active-bg-color);
        border-color: var(--tab-active-border-color);
        box-shadow: var(--tab-active-box-shadow, none);
      }
    }
  }
</style>
